import React from "react";

/**
 * Terms & Conditions — public page linked from Twilio A2P Campaign
 * Registration and site footer. Includes the SMS-program terms Twilio's
 * A2P vetting expects.
 */
export default function Terms() {
  return (
    <section
      data-testid="terms-page"
      className="mx-auto max-w-3xl px-5 sm:px-8 py-14 text-white/85 space-y-6 leading-relaxed"
    >
      <header>
        <div className="label-caps text-cyan text-xs">Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-white mt-2">Terms &amp; Conditions</h1>
        <p className="text-sm text-white/50 mt-2">Effective February 1, 2026</p>
      </header>

      <p>
        These Terms &amp; Conditions ("Terms") govern your use of Plenty
        Rydez LLC's ("Plenty Rydez", "we", "us") chauffeured-ride and
        self-drive rental services and website (<a className="text-cyan hover:underline" href="https://plenty-rydez.com">plenty-rydez.com</a>). By booking a ride, renting a vehicle,
        or opting into our SMS program, you agree to these Terms.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">1. SMS &amp; messaging program</h2>
      <p>
        Plenty Rydez uses SMS to deliver transactional messages related to
        your reservation: booking confirmations, rental agreement links,
        driver / manager follow-ups, and payment reminders.
      </p>
      <ul className="list-disc pl-6 space-y-2 text-white/80">
        <li><b>Consent:</b> By providing your mobile number during a booking, rental inquiry, or through our concierge team, you consent to receive SMS messages from Plenty Rydez at that number.</li>
        <li><b>Message frequency:</b> Message frequency varies based on your booking activity — typically 2–5 messages per reservation.</li>
        <li><b>Message and data rates may apply.</b> Standard carrier rates from your mobile provider apply to each message received.</li>
        <li><b>Opt-out:</b> Reply <span className="font-mono text-cyan">STOP</span> to any Plenty Rydez SMS at any time to cancel further messages. Reply <span className="font-mono text-cyan">HELP</span> for help, or contact <a className="text-cyan hover:underline" href="mailto:concierge@plenty-rydez.com">concierge@plenty-rydez.com</a>.</li>
        <li><b>Supported carriers:</b> Major US carriers (AT&amp;T, Verizon, T-Mobile, US Cellular, and their MVNOs). Carriers are not liable for delayed or undelivered messages.</li>
        <li><b>Non-sharing of mobile numbers:</b> Plenty Rydez does not share, sell, rent, or lease mobile numbers or SMS opt-in data with third parties for marketing purposes. See our <a className="text-cyan hover:underline" href="/privacy">Privacy Policy</a> for full details.</li>
      </ul>

      <h2 className="font-serif text-2xl text-white mt-8">2. Bookings &amp; payments</h2>
      <p>
        Chauffeured rides require a <b>50% deposit</b> at the time of
        booking, with the remaining balance charged before or at pickup.
        Self-drive rentals require a signed digital rental agreement, a
        valid driver's license, and a refundable security / damage deposit
        set by Plenty Rydez per vehicle.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">3. Cancellations &amp; refunds</h2>
      <p>
        Chauffeured ride deposits are refundable if the ride is cancelled
        more than 48 hours before pickup. Cancellations within 48 hours
        forfeit the 50% deposit. Self-drive rental deposits are refunded
        within 72 hours of a clean return handoff, minus any documented
        mileage overages, fuel shortages, tolls, or damages.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">4. Rental vehicle use</h2>
      <p>
        The renter is responsible for operating the vehicle in accordance
        with all applicable traffic and insurance laws. Additional drivers
        must be declared and approved. Any tickets, tolls, or violations
        during the rental period are the renter's responsibility.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">5. Liability</h2>
      <p>
        Plenty Rydez's liability is limited to the amount paid for the
        specific reservation. We are not liable for indirect, incidental,
        or consequential damages.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">6. Changes to these Terms</h2>
      <p>
        We may update these Terms as our services evolve. Continued use of
        Plenty Rydez after any update constitutes acceptance of the revised
        Terms.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">7. Contact</h2>
      <p>
        Plenty Rydez LLC · Serving NY / NJ / PA / DMV / CT<br />
        Email: <a className="text-cyan hover:underline" href="mailto:concierge@plenty-rydez.com">concierge@plenty-rydez.com</a><br />
        Phone: <a className="text-cyan hover:underline" href="tel:+17188409084">(718) 840-9084</a>
      </p>
    </section>
  );
}
